import { watch } from "vue";
import {
  useOneSignal,
  type IOneSignalOneSignal,
  type SubscriptionChangeEvent,
  type UserChangeEvent,
} from "@onesignal/onesignal-vue3";
import {
  GetPushRuntimeConfig,
  RegisterPushSubscription,
  UnregisterPushSubscription,
} from "@/api/pushNotificationsApi";
import { isPwaRuntimeEligible } from "@/services/pwaEligibility";
import { useAuthStore } from "@/stores/auth.store";
import type {
  PushNotificationClientStatus,
  PushRuntimeConfig,
} from "@/type/pushNotifications";

type OneSignalSdk = IOneSignalOneSignal;
type OneSignalPushSubscriptionChange = SubscriptionChangeEvent;
type OneSignalUserChange = UserChangeEvent;
const OneSignal = useOneSignal();

let lifecycleStarted = false;
let sdkInstance: OneSignalSdk | null = null;
let sdkInitPromise: Promise<OneSignalSdk | null> | null = null;
let initializingAppId: string | null = null;
let initializedAppId: string | null = null;
let activeExternalId: string | null = null;
let identityAttached = false;
// `identityAttached` only means `login()` resolved. Sends are targeted by
// external ID, so the backend can only be told about a subscription once the
// SDK itself confirms the alias landed.
let identityVerified = false;
let subscriptionListenerBound = false;
let manualSubscriptionAction = false;
let identityQueue: Promise<unknown> = Promise.resolve();

function isLocalOneSignalOrigin() {
  if (typeof window === "undefined")
    return false;

  return [
    "localhost",
    "127.0.0.1",
    "::1",
    "[::1]",
  ].includes(window.location.hostname);
}

function subscriptionId(oneSignal: OneSignalSdk) {
  return String(oneSignal.User?.PushSubscription?.id ?? "").trim();
}

function subscriptionToken(oneSignal: OneSignalSdk) {
  return String(oneSignal.User?.PushSubscription?.token ?? "").trim();
}

function normalizeSubscriptionId(value: unknown) {
  return String(value ?? "").trim();
}

function hasSendableSubscription(oneSignal: OneSignalSdk) {
  const subscription = oneSignal.User?.PushSubscription;

  return Boolean(
    subscription?.id
    && subscription.token
    && subscription.optedIn === true,
  );
}

function normalizedPermission(
  oneSignal: OneSignalSdk,
): NotificationPermission | boolean {
  const nativePermission = oneSignal.Notifications?.permissionNative;
  if (nativePermission !== undefined)
    return nativePermission;

  const permission = oneSignal.Notifications?.permission;

  if (permission !== undefined)
    return permission;

  return typeof Notification === "undefined"
    ? false
    : Notification.permission;
}

function permissionGranted(oneSignal: OneSignalSdk) {
  const permission = normalizedPermission(oneSignal);

  return permission === true || permission === "granted";
}

function currentExternalId(oneSignal: OneSignalSdk) {
  return String(oneSignal.User?.externalId ?? "").trim();
}

/**
 * `login()` resolves once the identity operation is accepted, which can happen
 * before the SDK repopulates `User.externalId`. Prefer the SDK's own change
 * event, but keep a bounded poll as well: listeners are registered through the
 * SDK's deferred queue, so a change that already landed can be missed, and
 * treating a linked device as unlinked would disable working notifications.
 */
function waitForExternalId(
  oneSignal: OneSignalSdk,
  externalId: string,
  timeoutMs = 5_000,
): Promise<boolean> {
  if (currentExternalId(oneSignal) === externalId)
    return Promise.resolve(true);

  return new Promise(resolve => {
    let settled = false;

    function finish(linked: boolean) {
      if (settled)
        return;

      settled = true;
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      oneSignal.User?.removeEventListener?.("change", listener);
      resolve(linked);
    }

    function listener(event?: OneSignalUserChange) {
      const current = String(
        event?.current?.externalId ?? oneSignal.User?.externalId ?? "",
      ).trim();

      if (current === externalId)
        finish(true);
    }

    const poll = window.setInterval(() => {
      if (currentExternalId(oneSignal) === externalId)
        finish(true);
    }, 250);
    const timeout = window.setTimeout(() => finish(false), timeoutMs);

    oneSignal.User?.addEventListener?.("change", listener);
  });
}

async function ensureOneSignalWorkerRegistration(config: PushRuntimeConfig) {
  const workerUrl = new URL(
    config.serviceWorkerPath.replace(/^\/+/, ""),
    `${window.location.origin}/`,
  );
  const scopeUrl = new URL(config.serviceWorkerScope, window.location.origin);

  if (
    workerUrl.origin !== window.location.origin
    || scopeUrl.origin !== window.location.origin
  ) {
    throw new Error("OneSignal worker path and scope must be same-origin");
  }

  const response = await fetch(workerUrl, {
    cache: "no-store",
    redirect: "error",
  });
  const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";

  if (!response.ok) {
    throw new Error(
      `OneSignal worker returned HTTP ${response.status}`,
    );
  }

  if (!contentType.includes("javascript")) {
    throw new Error(
      `OneSignal worker returned an invalid content type: ${contentType || "missing"}`,
    );
  }

  return navigator.serviceWorker.register(workerUrl, {
    scope: scopeUrl.pathname,
  });
}

function clientStatus(
  config: PushRuntimeConfig,
  oneSignal: OneSignalSdk | null,
  supported: boolean | null,
): PushNotificationClientStatus {
  const subscription = oneSignal?.User?.PushSubscription;

  return {
    runtimeEligible: isPwaRuntimeEligible(),
    configured: Boolean(
      config.enabled
      && config.webEnabled
      && config.appId
      && config.externalId,
    ),
    supported,
    permission: oneSignal
      ? normalizedPermission(oneSignal)
      : typeof Notification === "undefined"
        ? false
        : Notification.permission,
    externalId: config.externalId,
    // Reported by the provider, not echoed from the config: a browser that
    // never linked the alias cannot be sent to, however healthy it looks.
    identityLinked: Boolean(
      config.externalId
      && oneSignal
      && currentExternalId(oneSignal) === config.externalId,
    ),
    oneSignalId: oneSignal?.User?.onesignalId ?? null,
    subscriptionId: String(subscription?.id ?? "").trim() || null,
    tokenAvailable: Boolean(String(subscription?.token ?? "").trim()),
    optedIn: subscription?.optedIn === true,
    appId: config.appId,
    serviceWorkerPath: config.serviceWorkerPath,
    serviceWorkerScope: config.serviceWorkerScope,
  };
}

async function prepareOneSignal(config: PushRuntimeConfig) {
  if (!useAuthStore().isAuthenticated) {
    return {
      oneSignal: null,
      supported: null,
    };
  }

  if (!isPwaRuntimeEligible()) {
    return {
      oneSignal: null,
      supported: null,
    };
  }

  if (
    !config.enabled
    || !config.webEnabled
    || !config.appId
    || !config.externalId
  ) {
    return {
      oneSignal: null,
      supported: null,
    };
  }

  const supported = OneSignal.Notifications.isPushSupported();
  if (!supported) {
    return {
      oneSignal: null,
      supported: false,
    };
  }

  const oneSignal = await ensureOneSignal(config);
  if (!oneSignal || !useAuthStore().isAuthenticated) {
    return {
      oneSignal: null,
      supported: null,
    };
  }

  const desiredExternalId = config.externalId;
  const linkedExternalId = currentExternalId(oneSignal);
  const alreadyLinked = identityAttached
    && identityVerified
    && activeExternalId === desiredExternalId
    && linkedExternalId === desiredExternalId;

  if (!alreadyLinked) {
    // A different identity on this browser (a shared device, or an interrupted
    // logout) has to be detached first. OneSignal otherwise keeps the previous
    // alias and every send to the new user is rejected.
    if (linkedExternalId && linkedExternalId !== desiredExternalId)
      await oneSignal.logout();

    identityAttached = false;
    identityVerified = false;
    activeExternalId = null;

    await oneSignal.login(desiredExternalId);
    identityAttached = true;
    identityVerified = await waitForExternalId(oneSignal, desiredExternalId);

    if (!identityVerified) {
      console.warn(
        "Push identity did not link: the notification provider reports",
        currentExternalId(oneSignal) || "no external ID",
        "instead of",
        desiredExternalId,
      );

      return {
        oneSignal: null,
        supported: true,
      };
    }

    activeExternalId = desiredExternalId;
  }

  bindSubscriptionListener(oneSignal);

  return {
    oneSignal,
    supported: true,
  };
}

async function syncBackendSubscription(
  oneSignal: OneSignalSdk,
  knownConfig?: PushRuntimeConfig,
  change?: OneSignalPushSubscriptionChange,
  previousSubscriptionId?: string,
) {
  // The change listener fires independently of `prepareOneSignal`, so the
  // verified-identity gate has to be re-checked here too.
  if (!activeExternalId || !identityAttached || !identityVerified)
    return;

  // Subscription IDs are scoped to a OneSignal app. Re-read the authoritative
  // backend configuration for observer-driven updates so an open SPA cannot
  // register an ID from an SDK instance initialized with an older App ID.
  const config = knownConfig ?? await GetPushRuntimeConfig();
  if (
    !config.enabled
    || !config.webEnabled
    || !config.appId
    || config.appId !== initializedAppId
  ) {
    activeExternalId = null;
    console.warn(
      "Push subscription synchronization skipped: reload to apply the current backend App ID",
    );
    return;
  }

  if (!config.externalId || config.externalId !== activeExternalId) {
    console.warn(
      "Push subscription synchronization skipped: the authenticated push identity changed",
    );
    return;
  }

  const subscription = oneSignal.User?.PushSubscription;
  const current = change?.current ?? subscription;
  const id = normalizeSubscriptionId(current?.id);
  const previousId = normalizeSubscriptionId(
    change?.previous?.id ?? previousSubscriptionId,
  );

  if (current?.optedIn === false) {
    const idToDisable = id || previousId;
    if (idToDisable)
      await UnregisterPushSubscription(idToDisable);
    return;
  }

  // A provider rotation replaces only this browser's registration. The shared
  // user identity may still have other active subscription rows.
  if (previousId && previousId !== id)
    await UnregisterPushSubscription(previousId);

  const token = String(current?.token ?? "").trim();
  if (!id || !token || current?.optedIn !== true)
    return;

  await RegisterPushSubscription({
    subscriptionId: id,
    oneSignalId: oneSignal.User?.onesignalId ?? null,
    platform: "web",
    deviceId: navigator.userAgent,
    permission: normalizedPermission(oneSignal),
    token,
    optedIn: true,
  });
}

function waitForSubscribedPush(
  oneSignal: OneSignalSdk,
  timeoutMs = 10_000,
): Promise<boolean> {
  if (hasSendableSubscription(oneSignal))
    return Promise.resolve(true);

  const subscription = oneSignal.User?.PushSubscription;
  if (!subscription?.addEventListener)
    return Promise.resolve(false);

  return new Promise(resolve => {
    const timeout = window.setTimeout(() => {
      subscription.removeEventListener?.("change", listener);
      resolve(false);
    }, timeoutMs);

    function listener(event?: OneSignalPushSubscriptionChange) {
      const current = event?.current ?? oneSignal.User?.PushSubscription;
      if (!current?.id || !current.token || current.optedIn !== true)
        return;

      window.clearTimeout(timeout);
      subscription?.removeEventListener?.("change", listener);
      resolve(true);
    }

    subscription.addEventListener?.("change", listener);
  });
}

function bindSubscriptionListener(oneSignal: OneSignalSdk) {
  if (subscriptionListenerBound)
    return;

  const subscription = oneSignal.User?.PushSubscription;
  if (!subscription?.addEventListener)
    return;

  subscriptionListenerBound = true;
  subscription.addEventListener("change", async event => {
    if (manualSubscriptionAction)
      return;

    try {
      await syncBackendSubscription(oneSignal, undefined, event);
    }
    catch (error) {
      console.warn("Push subscription synchronization failed", error);
    }
  });
}

function ensureOneSignal(config: PushRuntimeConfig) {
  if (!config.appId)
    return Promise.resolve(null);

  const appId = config.appId;

  if (sdkInstance) {
    if (initializedAppId !== appId) {
      activeExternalId = null;
      console.warn(
        "Push configuration changed after OneSignal initialized; reload to apply the new App ID",
      );

      return Promise.resolve(null);
    }

    return Promise.resolve(sdkInstance);
  }

  if (sdkInitPromise) {
    if (initializingAppId !== appId) {
      activeExternalId = null;
      console.warn(
        "Push configuration changed while OneSignal was initializing; reload to apply the new App ID",
      );

      return Promise.resolve(null);
    }

    return sdkInitPromise;
  }

  initializingAppId = appId;
  sdkInitPromise = (async () => {
    try {
      await OneSignal.init({
        appId,
        allowLocalhostAsSecureOrigin: isLocalOneSignalOrigin(),
        serviceWorkerPath: config.serviceWorkerPath,
        serviceWorkerParam: {
          scope: config.serviceWorkerScope,
        },
      });

      sdkInstance = OneSignal;
      initializedAppId = appId;
      initializingAppId = null;

      return OneSignal;
    }
    catch (error) {
      console.warn("OneSignal initialization failed", error);
      initializingAppId = null;
      sdkInitPromise = null;

      return null;
    }
  })();

  return sdkInitPromise;
}

// `login()` and `logout()` are both dispatched through the SDK's deferred
// queue, so a fast logout -> login on a shared device can let the outgoing
// logout strip the incoming user's alias. Chain every identity transition
// through a single promise so they can never interleave.
function queueIdentityTransition<T>(task: () => Promise<T>): Promise<T> {
  const next = identityQueue.catch(() => {}).then(task);

  identityQueue = next.catch(() => {});

  return next;
}

export async function initPushNotifications() {
  try {
    const config = await GetPushRuntimeConfig();

    const { oneSignal, supported } = await queueIdentityTransition(
      () => prepareOneSignal(config),
    );
    if (!oneSignal || !supported)
      return;

    // Startup never prompts. It only restores identity and synchronizes a
    // subscription the user has already enabled from the Profile tab.
    if (hasSendableSubscription(oneSignal))
      await syncBackendSubscription(oneSignal, config);
  }
  catch (error) {
    console.warn("Push notification initialization failed", error);
  }
}

export async function getPushNotificationClientStatus():
Promise<PushNotificationClientStatus> {
  const config = await GetPushRuntimeConfig();
  const { oneSignal, supported } = await queueIdentityTransition(
    () => prepareOneSignal(config),
  );

  return clientStatus(config, oneSignal, supported);
}

export async function subscribeToPushNotifications():
Promise<PushNotificationClientStatus> {
  manualSubscriptionAction = true;

  try {
    const config = await GetPushRuntimeConfig();
    const { oneSignal, supported } = await queueIdentityTransition(
      () => prepareOneSignal(config),
    );

    if (!oneSignal || !supported)
      return clientStatus(config, oneSignal, supported);

    const previousSubscriptionId = subscriptionId(oneSignal);

    await oneSignal.User?.PushSubscription?.optIn?.();

    if (!(await waitForSubscribedPush(oneSignal))) {
      const subscription = oneSignal.User?.PushSubscription;
      const staleOptIn = permissionGranted(oneSignal)
        && subscription?.optedIn === true
        && !subscriptionToken(oneSignal);

      if (!staleOptIn)
        return clientStatus(config, oneSignal, supported);

      // OneSignal can retain an opted-in user/subscription record after its
      // scoped browser registration or push token has been removed. optIn()
      // then becomes a no-op. Recreate the verified worker registration and
      // cycle that stale state once from this explicit user action.
      await ensureOneSignalWorkerRegistration(config);
      await subscription?.optOut?.();
      await subscription?.optIn?.();

      if (!(await waitForSubscribedPush(oneSignal)))
        return clientStatus(config, oneSignal, supported);
    }

    await syncBackendSubscription(
      oneSignal,
      config,
      undefined,
      previousSubscriptionId,
    );

    return clientStatus(config, oneSignal, supported);
  }
  finally {
    manualSubscriptionAction = false;
  }
}

export async function unsubscribeFromPushNotifications():
Promise<PushNotificationClientStatus> {
  manualSubscriptionAction = true;

  try {
    const config = await GetPushRuntimeConfig();
    const { oneSignal, supported } = await queueIdentityTransition(
      () => prepareOneSignal(config),
    );

    // Opting out needs no verified identity: it is a local browser action plus
    // a row deletion keyed on subscription ID. Falling back to the initialized
    // SDK keeps this reachable for a browser whose alias never linked, which
    // would otherwise be stuck opted in with no way to switch it off.
    const sdk = oneSignal ?? sdkInstance;
    if (!sdk)
      return clientStatus(config, oneSignal, supported);

    const id = subscriptionId(sdk);

    await sdk.User?.PushSubscription?.optOut?.();
    if (id)
      await UnregisterPushSubscription(id);

    return clientStatus(config, oneSignal, supported);
  }
  finally {
    manualSubscriptionAction = false;
  }
}

async function detachPushIdentity() {
  identityAttached = false;
  identityVerified = false;
  activeExternalId = null;

  try {
    await sdkInstance?.logout?.();
  }
  catch (error) {
    console.warn("Push identity logout failed", error);
  }
}

export function setupPushNotificationLifecycle() {
  if (lifecycleStarted)
    return;

  lifecycleStarted = true;
  const auth = useAuthStore();

  watch(
    () => auth.isAuthenticated,
    (authenticated) => {
      if (authenticated)
        void initPushNotifications();
      else
        void queueIdentityTransition(detachPushIdentity);
    },
    { immediate: true },
  );
}
